/**
 * Low-level pieces shared between dtpSelect.js and dtpCombobox.js.
 */

import { createIcon } from '../../../icons';

export function optionDomId(namespace, optionId) {
  return `${namespace}-option-${optionId}`;
}

function applyTriggerAria(trigger, { namespace, labelId }) {
  trigger.id = `${namespace}-trigger`;
  trigger.setAttribute('role', 'combobox');
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-controls', `${namespace}-listbox`);
  if (labelId) {
    trigger.setAttribute('aria-labelledby', `${labelId} ${trigger.id}`);
  }
}

export function createTrigger({ namespace, labelId }) {
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'dtp-select__trigger';
  applyTriggerAria(trigger, { namespace, labelId });

  const valueLabel = document.createElement('span');
  valueLabel.className = 'dtp-select__value';
  trigger.appendChild(valueLabel);

  const chevron = createIcon('chevron', 'dtp-select__chevron');
  trigger.appendChild(chevron);

  return { trigger, valueLabel };
}

// Combobox variant of createTrigger
export function createComboboxTrigger({ namespace, labelId, placeholder }) {
  const trigger = document.createElement('input');
  trigger.type = 'text';
  trigger.className = 'dtp-select__trigger';
  trigger.autocomplete = 'off';
  trigger.spellcheck = false;
  applyTriggerAria(trigger, { namespace, labelId });
  trigger.setAttribute('aria-autocomplete', 'list');
  if (placeholder) {
    trigger.placeholder = placeholder;
  }
  return trigger;
}

// Shared disabled visual/aria state for all trigger variants.
export function setTriggerDisabled(container, trigger, disabled) {
  trigger.disabled = disabled;
  container.classList.toggle('dtp-select--disabled', disabled);
}

export function createListbox({ namespace, labelId, multiselectable = false }) {
  const listbox = document.createElement('div');
  listbox.className = 'dtp-select__listbox';
  listbox.id = `${namespace}-listbox`;
  listbox.setAttribute('role', 'listbox');
  if (multiselectable) {
    listbox.setAttribute('aria-multiselectable', 'true');
  }
  listbox.tabIndex = -1;
  if (labelId) {
    listbox.setAttribute('aria-labelledby', labelId);
  }
  return listbox;
}

export function setActiveIndex({ trigger, optionElements, index, scrollIntoView = true }) {
  optionElements.forEach((optionEl, i) => {
    optionEl.classList.toggle('dtp-select__option--active', i === index);
  });
  if (index >= 0) {
    trigger.setAttribute('aria-activedescendant', optionElements[index].id);
    if (scrollIntoView) {
      optionElements[index].scrollIntoView({ block: 'nearest' });
    }
  } else {
    trigger.removeAttribute('aria-activedescendant');
  }
}

// Document click listener is attached only while the listbox is open.
export function createOutsideClickHandler(container, closeListbox) {
  const handleClick = (e) => {
    if (!container.contains(e.target)) {
      closeListbox({ restoreFocus: false });
    }
  };
  return {
    enable: () => document.addEventListener('click', handleClick),
    disable: () => document.removeEventListener('click', handleClick),
  };
}
